import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Activity, Check, Sparkles, Crown, LogOut, Clock, Rocket } from "lucide-react";
import { useAuth, homeForRole } from "@/store/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  { lookup: "veloclin_solo", name: "VeloClin", price: "R$ 199", popular: false, solo: true,
    features: ["Telemedicina de alta performance", "Sua página de vendas de consultas", "Receitas e exames digitais (Memed)", "Recebimento direto no seu Pix (Asaas)", "Agenda e prontuário ágil", "Copiloto no WhatsApp", "Sem comissão por consulta — você fica com 100%"] },
];

export default function Assinar() {
  const { currentUser, blocked, daysLeft, subscriptionStatus, logout, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [appliedCode, setAppliedCode] = useState("");
  const [applyMsg, setApplyMsg] = useState("");
  const [billingType, setBillingType] = useState("PIX");
  const [cpf, setCpf] = useState("");
  const [crm, setCrm] = useState("");
  const [founders, setFounders] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/founders/status`).then((r) => r.json()).then(setFounders).catch(() => {});
  }, []);

  const applyCoupon = async () => {
    const code = coupon.trim().toUpperCase();
    if (!code) return;
    try {
      const token = localStorage.getItem("veloclin_token");
      const r = await fetch(`${API}/api/coupons/apply`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Cupom inválido");
      if (d.result.applied === "free_days") {
        toast.success(`${d.result.free_days} dias grátis ativados!`, { description: "Todos os seus dados foram mantidos." });
        await refreshMe();
        navigate(homeForRole(d.user.role), { replace: true });
      } else {
        setAppliedCode(code);
        setApplyMsg(`Cupom ${code}: ${d.result.percent_off}% de desconto na 1ª mensalidade. Escolha um plano abaixo.`);
        toast.success(`Cupom ${code} aplicado`, { description: `${d.result.percent_off}% na primeira mensalidade.` });
      }
    } catch (e) {
      toast.error("Não foi possível aplicar", { description: e.message });
    }
  };

  if (currentUser === null) return null;
  if (!currentUser) {
    navigate("/cadastro", { replace: true });
    return null;
  }

  const checkout = async (lookup) => {
    const doc = cpf.replace(/\D/g, "");
    if (doc.length !== 11 && doc.length !== 14) {
      toast.error("Informe um CPF ou CNPJ válido para a cobrança.");
      return;
    }
    if (!crm.trim()) {
      toast.error("Informe seu número de CRM (será validado pela nossa equipe).");
      return;
    }
    setLoading(lookup);
    try {
      const token = localStorage.getItem("veloclin_token");
      await fetch(`${API}/api/account/crm`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ crm: crm.trim() }),
      });
      const r = await fetch(`${API}/api/billing/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ lookup_key: lookup, billing_type: billingType, cpf_cnpj: doc }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Falha ao iniciar a cobrança.");
      if (d.invoiceUrl) { window.location.href = d.invoiceUrl; return; }
      toast.success("Cobrança criada", { description: "Sua fatura está sendo gerada. Atualize em instantes." });
      setLoading(null);
    } catch (err) {
      toast.error("Erro na cobrança", { description: err.message });
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-blue-950 px-5 py-10">
      <div className="mx-auto max-w-4xl">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/veloclin-logo.png" alt="VeloClin" data-testid="brand-logo" className="h-12 w-auto rounded-full bg-white p-1" />
          </div>
          <button data-testid="assinar-logout" onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm font-semibold text-slate-200 hover:bg-white/10">
            <LogOut className="h-4 w-4" /> Sair
          </button>
        </div>

        <div className="mb-8 text-center">
          {blocked ? (
            <div data-testid="trial-expired-banner" className="mx-auto mb-4 inline-flex items-center gap-2 rounded-full border border-rose-400/30 bg-rose-500/10 px-4 py-1.5 text-sm font-semibold text-rose-300">
              <Clock className="h-4 w-4" /> Seu teste gratuito terminou
            </div>
          ) : (
            <div className="mx-auto mb-4 inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-4 py-1.5 text-sm font-semibold text-emerald-300">
              <Clock className="h-4 w-4" /> {subscriptionStatus === "active" ? "Assinatura ativa" : `Teste ativo · faltam ${daysLeft} dias`}
            </div>
          )}
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            {blocked ? "Contrate para continuar usando o VeloClin" : "Escolha seu plano"}
          </h1>
          <p className="mt-2 text-sm text-slate-400">Todos os dados criados no período de teste são mantidos após a contratação.</p>
        </div>

        <div className="mx-auto mb-8 max-w-md rounded-2xl border border-white/10 bg-white/[0.04] p-4">
          <label className="text-xs font-semibold text-slate-300">Tem um cupom de desconto?</label>
          <div className="mt-2 flex gap-2">
            <input data-testid="assinar-coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="VELO50, VELO30D…" className="flex-1 rounded-xl border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <button data-testid="assinar-coupon-apply" onClick={applyCoupon} className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-700">Aplicar</button>
          </div>
          {applyMsg && <p data-testid="assinar-coupon-msg" className="mt-2 text-xs font-semibold text-emerald-300">{applyMsg}</p>}
        </div>

        <div className="mx-auto mb-8 max-w-md rounded-2xl border border-white/10 bg-white/[0.04] p-4">
          <label className="text-xs font-semibold text-slate-300">CPF ou CNPJ do responsável</label>
          <input data-testid="assinar-cpf-input" value={cpf} onChange={(e) => setCpf(e.target.value)} inputMode="numeric" placeholder="Somente números" className="mt-2 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <label className="mt-3 block text-xs font-semibold text-slate-300">Número do CRM (validado manualmente pela nossa equipe)</label>
          <input data-testid="assinar-crm-input" value={crm} onChange={(e) => setCrm(e.target.value)} placeholder="Ex: 123456/SP" className="mt-2 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <label className="mt-3 block text-xs font-semibold text-slate-300">Forma de pagamento</label>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {[["PIX", "Pix"], ["BOLETO", "Boleto"], ["CREDIT_CARD", "Cartão"]].map(([v, l]) => (
              <button key={v} data-testid={`assinar-billing-${v}`} onClick={() => setBillingType(v)}
                className={`rounded-xl px-3 py-2 text-xs font-bold transition-colors ${billingType === v ? "bg-emerald-600 text-white" : "border border-white/15 bg-white/5 text-slate-300 hover:bg-white/10"}`}>{l}</button>
            ))}
          </div>
        </div>

        <div className="mx-auto grid max-w-md items-start gap-5">
          {PLANS.map((p) => {
            const foundersActive = p.solo && founders?.active;
            return (
            <motion.div key={p.lookup} data-testid={`assinar-plan-${p.lookup}`} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              className={`relative rounded-3xl border p-7 ${foundersActive ? "border-amber-400 bg-white shadow-[0_20px_50px_-20px_rgba(245,158,11,0.6)]" : p.popular ? "border-blue-500 bg-white shadow-[0_20px_50px_-20px_rgba(37,99,235,0.6)]" : "border-white/10 bg-white/[0.04]"}`}>
              {foundersActive && (
                <span data-testid="assinar-founder-badge" className="absolute -top-3 left-1/2 flex -translate-x-1/2 items-center gap-1 whitespace-nowrap rounded-full bg-amber-500 px-3 py-1 text-xs font-bold text-white"><Rocket className="h-3 w-3" /> Fundadores VeloClin</span>
              )}
              {!foundersActive && p.popular && (
                <span className="absolute -top-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-blue-600 px-3 py-1 text-xs font-bold text-white"><Sparkles className="h-3 w-3" /> Mais Popular</span>
              )}
              <div className={`flex items-center gap-2 ${foundersActive || p.popular ? "text-slate-800" : "text-slate-200"}`}>
                {p.popular ? <Crown className="h-5 w-5 text-amber-400" /> : <Activity className="h-5 w-5" />}
                <h3 className="font-heading text-xl font-bold">{p.name}</h3>
              </div>
              {foundersActive ? (
                <>
                  <div className="mt-4 flex items-end gap-2">
                    <span data-testid="assinar-founder-price" className="font-mono-num text-4xl font-extrabold text-slate-900">R$ {founders.price}</span>
                    <span className="mb-1 text-sm text-slate-500">/mês</span>
                    <span className="mb-1 text-sm font-semibold text-slate-400 line-through">R$ {founders.full_price}</span>
                  </div>
                  <p data-testid="assinar-founder-remaining" className="mt-1 text-xs font-bold text-amber-600">Restam {founders.remaining} de {founders.limit} vagas · R$ {founders.price}/mês vitalício</p>
                </>
              ) : (
                <div className="mt-4 flex items-end gap-1">
                  <span className={`font-mono-num text-4xl font-extrabold ${p.popular ? "text-slate-900" : "text-white"}`}>{p.price}</span>
                  <span className={`mb-1 text-sm ${p.popular ? "text-slate-500" : "text-slate-400"}`}>/mês</span>
                </div>
              )}
              <ul className="mt-6 space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className={`flex items-start gap-2.5 text-sm ${foundersActive || p.popular ? "text-slate-600" : "text-slate-300"}`}>
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> {f}
                  </li>
                ))}
              </ul>
              <button data-testid={`assinar-cta-${p.lookup}`} onClick={() => checkout(p.lookup)} disabled={loading}
                className={`mt-7 flex w-full items-center justify-center rounded-xl px-5 py-3.5 text-sm font-bold transition-colors disabled:opacity-60 ${foundersActive ? "bg-amber-500 text-white hover:bg-amber-600" : p.popular ? "bg-blue-600 text-white hover:bg-blue-700" : "border border-white/15 bg-white/10 text-white hover:bg-white/20"}`}>
                {loading === p.lookup ? "Redirecionando..." : `Assinar ${p.name}`}
              </button>
            </motion.div>
          );})}
        </div>
        <p className="mt-6 text-center text-xs text-slate-500">Pagamento seguro via Asaas · Pix, Boleto ou Cartão de Crédito</p>
      </div>
    </div>
  );
}
